<template>
  <div class="video-room">
    <!-- Video Background Component -->
    <VideoPlayer
      ref="backgroundVideoPlayer"
      :videoTrack="backgroundVideoTrack"
      class="video-background"
      :blur="hasNudity"
    />

    <!-- Foreground Content Wrapper -->
    <div class="foreground-content">
      <VideoRoomHeader
        title=""
        room-id=""
        :host-avatar="require('@/assets/images/auth/female-avatar.png')"
        :participant-count="123"
        :current-progress="9823"
        :goal-target="15000"
        :gift-givers="[]"
        @settings="handleSettings"
        @close-stream="endStream"
      />

      <!-- Remove old local video container -->
      <!-- <div ref="localPlayerContainer" class="local-video-container"></div> -->

      <VideoRoomChat
        :messages="messages"
        :is-host="isHost"
        :available-gifts="availableGifts"
        @send-message="handleSendMessage"
        @open-gift-panel="openGiftPanel"
        @send-quick-gift="handleSendQuickGift"
      />

      <VideoRoomControls
        v-if="isHost"
        @toggle-mic="handleMicToggle"
      />

      <!-- Hediye satışı kaldırıldı — oda hediye modalı devre dışı
      <VideoRoomGiftModal
        ref="giftModal"
        :host-name="mainHost ? mainHost.name : ''"
      />
      -->

      <!-- Host Settings Panel -->
      <HostSettingsPanel
        v-if="roomInfo"
        ref="hostSettingsPanel"
        :is-host-muted="isMuted"
        :channel-id="roomInfo ? roomInfo.channelId : null"
        @close-stream="endStream"
        @toggle-mic="handleMicToggle"
        @mute-participant="handleMuteParticipant"
        @unmute-participant="handleUnmuteParticipant"
        @kick-participant="handleKickParticipant"
        @panel-opened="loadGiftSenders"
      />

      <!-- Hediye satışı kaldırıldı — hediye animasyonu devre dışı
      <GiftAnimationOverlay
        ref="giftOverlay"
      />
      -->

      <!-- Speaker Request Modal -->
      <SpeakerRequestModal
        ref="speakerRequestModal"
        :visible="showSpeakerRequestModal"
        :request="firstPendingRequest"
        @accept="approveRequest"
        @reject="rejectRequest"
      />

      <!-- Kicked Info Modal -->
      <b-modal
        id="kicked-info-modal"
        v-model="showKickedModal"
        centered
        hide-header
        hide-footer
        hide-header-close
        no-close-on-backdrop
        no-close-on-esc
      >
        <div class="text-center p-2">
          <h5 class="mb-1">{{ $t('videoRoom.youWereKickedTitle') || 'Bilgi' }}</h5>
          <p class="mb-2">{{ $t('videoRoom.youWereKicked') || 'Odadan atıldınız.' }}</p>
          <b-button
            variant="primary"
            @click="handleKickedModalClose"
          >
            {{ $t('Okay') || 'Tamam' }}
          </b-button>
        </div>
      </b-modal>

      <!-- Generic Error Modal -->
      <GenericErrorModal ref="errorModal" />

      <!-- Purchase Credits Modal -->
      <purchase-credits-modal ref="purchaseCreditsModal" />

    </div> <!-- End Foreground Content Wrapper -->

  </div>
</template>

<script>
import VideoRoomHeader from './components/VideoRoomHeader.vue'
import VideoPlayer from './components/VideoPlayer.vue'
import VideoRoomChat from './components/VideoRoomChat.vue'
import VideoRoomControls from './components/VideoRoomControls.vue'
import VideoRoomGiftModal from './components/VideoRoomGiftModal.vue'
import HostSettingsPanel from './components/HostSettingsPanel.vue'
import GiftAnimationOverlay from './components/GiftAnimationOverlay.vue'
import SpeakerRequestModal from './components/SpeakerRequestModal.vue'
import GenericErrorModal from './components/GenericErrorModal.vue'
import PurchaseCreditsModal from './components/PurchaseCreditsModal.vue'
import { BModal, BButton } from 'bootstrap-vue'
import AgoraRTC from 'agora-rtc-sdk-ng'
// Import Vuex helpers
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import {
  getInitialSpeakersData,
  getVideoRoomGoalsService,
  leaveAgoraChannelService,
  endStreamService,
  sendHeartbeatRequest,
  requestToBecomeSpeakerService,
  approveSpeakerRequestService,
  rejectSpeakerRequestService,
  initializeAgoraClient,
  joinAgoraChannel,
  setAgoraClientRole,
  publishLocalAudioTrack,
  publishLocalVideoTrack,
  becomePublisherService,
  leaveRoomAsAudienceService,
  getGiftSendersListService,
  sendVideoRoomGift,
  toggleRemoteMuteService,
  subscribeVideoRoomService,
  sendCheckNudity,
} from '@/services/VideoRoomService'
import { showNotify, throwDefaultError } from '@/helpers/helpers'

const fallbackAvatar = require('@/assets/images/auth/female-avatar.png')

export default {
  name: 'VideoRoom',
  components: {
    VideoRoomHeader,
    VideoPlayer,
    // VideoRoomSpeakers, // Removed registration
    VideoRoomChat,
    VideoRoomControls,
    VideoRoomGiftModal,
    HostSettingsPanel,
    GiftAnimationOverlay,
    SpeakerRequestModal,
    GenericErrorModal,
    PurchaseCreditsModal,
    BModal,
    BButton,
  },

  data() {
    return {
      messages: [],
      agoraClient: null,
      localAudioTrack: null,
      localVideoTrack: null,
      remoteVideoTrack: null,
      agoraInitialized: false,
      heartbeatIntervalId: null,
      isEndingStream: false,
      showKickedModal: false,
      heartbeatFailCount: 0,
      nudityCheckIntervalId: null,
      hasNudity: false,
      nudityCheckIntervalMs: 5000,
      nudityCheckStarted: false,
    }
  },

  computed: {
    roomInfo() {
      const storeInfo = this.$store.state.videoRoom.roomInfo
      // console.log('Store roomInfo:', storeInfo)
      return storeInfo
    },
    ...mapGetters('videoRoom', {
      isHost: 'isHost',
      isMuted: 'isMuted',
      pendingSpeakerRequests: 'pendingSpeakerRequests',
      hasPendingSpeakerRequests: 'hasPendingSpeakerRequests',
      mainHost: 'mainHost',
      speakers: 'speakers',
      giftSenders: 'giftSenders',
      availableGifts: 'availableGifts',
    }),
    ...mapGetters('socket', {
      socketConnection: 'connection',
    }),
    ...mapState('videoRoom', {
      hostAgoraUid: state => state.hostAgoraUid,
    }),
    firstPendingRequest() {
      return this.pendingSpeakerRequests && this.pendingSpeakerRequests.length > 0
        ? this.pendingSpeakerRequests[0]
        : null
    },
    showSpeakerRequestModal: {
      get() {
        const shouldShow = !!this.firstPendingRequest && this.isHost
        return shouldShow
      },
      // We don't need a setter, visibility is controlled by firstPendingRequest
    },
    backgroundVideoTrack() {
      // Display local video if host, otherwise display remote video (if available)
      return this.isHost ? this.localVideoTrack : this.remoteVideoTrack
    },
  },

  async created() {
    // Log roomInfo from store on component creation
    // console.log('VideoRoom - created - Initial store roomInfo:', JSON.stringify(this.$store.state.videoRoom.roomInfo));

    // Check if we have route params and if roomInfo is currently empty in the store
    const routeChannelId = this.$route.params.channelId
    const routeAgoraUid = this.$route.params.agoraUid
    const storeRoomInfo = this.$store.state.videoRoom.roomInfo

    if (routeChannelId && routeAgoraUid && !storeRoomInfo) {
      this.loading = true // Show loading indicator
      try {
        // Commit hostAgoraUid from route
        const hostAgoraUidFromRoute = this.$route.params.agoraUid
        this.$store.commit('videoRoom/SET_HOST_AGORA_UID', hostAgoraUidFromRoute)

        // Call the service directly (pass host UID)
        const roomInfo = await subscribeVideoRoomService(routeChannelId, hostAgoraUidFromRoute)

        // Commit the result directly to the store
        this.$store.commit('videoRoom/SET_ROOM_INFO', roomInfo)

        // Fetch gifts after getting room info
        this.fetchAvailableGifts()
        // Agora initialization will be triggered by the watch on roomInfo
      } catch (error) {
        console.error('VideoRoom - created - Error calling subscribeVideoRoomService:', error)
        // Handle failure - e.g., show error message, redirect
        this.$toast({
          title: this.$t('Error') || 'Hata',
          text: error.message || this.$t('videoRoom.joinErrorGeneric') || 'Yayına katılırken bir sorun oluştu.',
          variant: 'danger',
        })
        // Redirect to set up or home page after error
        // this.$router.push({ name: 'video-room-setup' });
      } finally {
        this.loading = false // Hide loading indicator
      }
    } else if (storeRoomInfo) {
      // If store already has info (navigated normally), fetch gifts immediately
      this.fetchAvailableGifts()
      // Watcher on roomInfo will handle initAgora
    } else {
      console.warn('VideoRoom - created - No route params or store info found. Cannot initialize room.')
      // Redirect if no info is available at all
      // this.$router.push({ name: 'video-room-setup' })
    }
  },

  watch: {
    roomInfo: {
      async handler(newVal) {
        if (newVal && !this.agoraInitialized) {
          await this.initAgora()
          await this.fetchAndSetRoomDetails()
        }
      },
      immediate: true,
    },
    socketConnection: {
      handler(newConnection, oldConnection) {
        if (newConnection && !oldConnection) {
          this.setupSocketListeners()
        }
      },
      immediate: true,
    },
  },

  beforeDestroy() {
    if (this.heartbeatIntervalId) {
      clearInterval(this.heartbeatIntervalId)
      this.heartbeatIntervalId = null
    }
    if (this.nudityCheckIntervalId) {
      clearInterval(this.nudityCheckIntervalId)
      this.nudityCheckIntervalId = null
    }
    leaveAgoraChannelService(this.agoraClient, this.localAudioTrack, this.localVideoTrack)
  },

  methods: {
    ...mapActions('videoRoom', [
      'setIsHost',
      'setIsMuted',
      'setSpeakers',
      'addSpeaker',
      'removeSpeaker',
      'processUserLeft',
      'handleSpeakerRequestApproved',
      'handleSpeakerRequestRejected',
      'setRoomDetails',
      'updateSpeakingStatus',
      'updateSpeaker',
      'fetchAvailableGifts',
    ]),
    ...mapMutations('videoRoom', [
      'ADD_SPEAKER_REQUEST',
      'REMOVE_SPEAKER_REQUEST',
      'SET_MAIN_HOST',
      'SET_AUDIENCE_COUNT',
      'SET_GIFT_SENDERS',
    ]),

    handleSettings() {
      if (this.$refs.hostSettingsPanel) {
        this.$refs.hostSettingsPanel.openPanel()
      } else {
        console.error('HostSettingsPanel ref not found!')
      }
    },

    async handleRequestSpeak() {
      try {
        const success = await requestToBecomeSpeakerService()

        if (success) {
          showNotify(this.$toast, this.$t('requestToSpeakSent'), 'info')
        } else {
          console.warn('[SOCKET] Speaker request service call returned false (unexpected).')
          showNotify(this.$toast, this.$t('videoRoom.requestErrorUnknown') || this.$t('voiceRoom.requestErrorUnknown'), 'warning')
        }
      } catch (error) {
        console.error('[SOCKET] Error requesting speaker role via service:', error)
        const apiErrorMessage = error.response?.data?.header?.error?.message
        const defaultErrorMessage = this.$t('videoRoom.requestErrorGeneric') || this.$t('voiceRoom.requestErrorGeneric')

        if (apiErrorMessage && this.$refs.errorModal) {
          this.$refs.errorModal.openModal(this.$t('Error') || 'Hata', apiErrorMessage)
        } else {
          showNotify(this.$toast, apiErrorMessage || defaultErrorMessage, 'danger')
        }
      }
    },

    setupSocketListeners() {
      if (!this.socketConnection) {
        console.warn('[SOCKET] Cannot setup listeners, connection is not available yet.')
        return
      }
      console.log('[SOCKET] Connection available. Setting up streamEvent listener.')
      this.socketConnection.off('streamEvent').on('streamEvent', this.handleVideoRoomEvent)
    },

    handleVideoRoomEvent(data) {
      // console.log('[SOCKET Received Event]', data); // Log all incoming events
      if (!data || !data.eventType) {
        console.warn('[SOCKET] Received roomEvent without eventType:', data)
        return
      }

      switch (data.eventType) {
        case 'be_publisher_request':
          this.handleBePublisherRequest(data)
          break
        case 'publisher_joined':
          this.handlePublisherJoined(data)
          break
        case 'subscriber_joined':
          this.handleSubscriberJoined(data)
          break
        case 'user_left':
          this.handleUserLeftEvent(data)
          break
        case 'channel_terminated':
          this.handleChannelTerminated(data)
          break
        case 'chat_msg_sent':
          this.handleChatMsgSent(data)
          break
        case 'chat_history':
          this.handleChatHistory(data)
          break
        case 'user_kicked':
          this.handleUserKicked(data)
          break
        case 'publisher_kicked':
          this.handleUserKicked(data)
          break
        case 'voice_state_toggled':
          this.handleVoiceStateToggled(data)
          break
        case 'gift_sent':
          this.handleIncomingGift(data)
          break
        default:
          console.log(`[SOCKET] Unhandled roomEvent type: ${data.eventType}`)
      }
    },

    handleBePublisherRequest(data) {
      if (this.isHost && data.userId && data.userName) {
        const request = {
          userId: data.userId,
          userName: data.userName,
          userAvatar: null, // TODO: Get avatar from data if available
          userUuid: data.userUuid,
        }
        this.ADD_SPEAKER_REQUEST(request)

        // Log store state after commit (original debug log location)
        this.$nextTick(() => {
          // console.log('[handleBePublisherRequest] Committed ADD_SPEAKER_REQUEST. Current pending requests in store:', JSON.stringify(this.$store.state.videoRoom.pendingSpeakerRequests));
        })

        showNotify(this.$toast, this.$t('videoRoom.newSpeakerRequest', { name: request.userName }), 'info')
      } else {
        // console.log('[SOCKET] Ignoring be_publisher_request (not host or missing user data). isHost:', this.isHost);
      }
    },

    handlePublisherJoined(data) {
      if (!data || !data.userId) {
        console.warn('[SOCKET] Invalid data received for publisher_joined event. Missing userId.')
        return
      }

      const joiningUserId = data.userId

      if (this.mainHost && this.mainHost.id === joiningUserId) {
        console.error(`[SOCKET] User ${joiningUserId} is already the main host. Updating info (if necessary).`)
        return
      }

      const isAlreadySpeaker = this.speakers.some(s => s.id === joiningUserId)
      if (isAlreadySpeaker) {
        console.error(`[SOCKET] User ${joiningUserId} is already in the speakers list. Updating info (if necessary).`)
        return
      }

      const newSpeaker = {
        id: joiningUserId,
        uuid: data.userUuid || null,
        name: data.userName || 'Unknown',
        avatar: data.userImage || fallbackAvatar,
        isActive: true,
        isMuted: false,
        level: data.userBadgeLevel === 'boho_member' ? 10 : null,
        agoraUid: data.agoraUid || null,
      }

      this.addSpeaker(newSpeaker)

      const myUserId = this.$store.state.auth.user?.identifier
      if (!this.isHost && myUserId && myUserId === joiningUserId) {
        // console.log(`[SOCKET] Promotion detected for self (User ID: ${myUserId}). Attempting to become publisher...`);
        this.becomePublisher()
      }
    },

    // eslint-disable-next-line no-unused-vars
    handleSubscriberJoined(data) {
      // console.log('[SOCKET] Handling subscriber_joined:', data);
    },

    handleUserLeftEvent(data) {
      if (!data || !data.userId) {
        console.warn('[SOCKET] user_left event received without userId. Cannot process.')
        return
      }

      const leavingUserId = data.userId
      this.processUserLeft(leavingUserId)
    },

    async handleChannelTerminated(data) {
      console.warn('[SOCKET] Handling channel_terminated event:', data)

      // Prevent duplicate cleanup if already leaving
      if (this.isEndingStream) {
        console.error('[SOCKET] Channel terminated event received, but already ending stream. Skipping redundant cleanup.')
        return
      }
      this.isEndingStream = true // Mark as ending to prevent race conditions

      // Clear heartbeat immediately
      if (this.heartbeatIntervalId) {
        clearInterval(this.heartbeatIntervalId)
        this.heartbeatIntervalId = null
      }

      try {
        // Leave Agora channel cleanly (handles both host and audience)
        await leaveAgoraChannelService(this.agoraClient, this.localAudioTrack, this.localVideoTrack)

        // Clean up store
        this.$store.dispatch('videoRoom/setRoomInfo', null)

        // Redirect to home page (adjust route name if necessary)
        this.$router.push({ name: 'popular-users' })
      } catch (error) {
        console.error('[SOCKET] Error during cleanup after channel termination:', error)
        // Even if cleanup fails, try to redirect
        this.$router.push({ name: 'popular-users' })
      } finally {
        // Reset the flag, although component might be destroyed soon
        this.isEndingStream = false
      }
    },

    handleChatMsgSent(data) {
      // 1. Prevent processing own message duplication (already added optimistically)
      const currentUserId = this.$store.state.auth.user?.id
      if (data.userId === currentUserId) {
        return
      }

      // 2. Check if necessary data exists in the new structure
      if (data.userName && data.content) {
        const newMessage = {
          type: 'text',
          sender: {
            name: data.userName, // Use userName from data
            avatar: data.profileImg || fallbackAvatar, // Use profileImg from data
          },
          content: data.content, // Use content from data
        }
        this.messages.push(newMessage)
      } else {
        // Update warning message to reflect expected fields
        console.warn('[SOCKET] chat_msg_sent event received with missing userName or content data.', data)
      }
    },

    handleChatHistory(data) {
      if (data.messages && Array.isArray(data.messages)) {
        this.messages = data.messages.map(msg => ({
          type: 'text',
          sender: {
            name: msg.userName || 'Unknown',
            avatar: msg.userImage || fallbackAvatar,
          },
          content: msg.message,
        }))
      } else {
        console.warn('[SOCKET] chat_history event received without a valid messages array.')
        this.messages = []
      }
    },

    handleUserKicked(data) {
      const kickedUserId = data.kickedUserId || data.userId

      if (!kickedUserId) {
        console.warn('[SOCKET] user_kicked event received without kickedUserId. Cannot process.')
        return
      }

      const currentUserId = this.$store.state.auth.user?.identifier

      if (kickedUserId === currentUserId) {
        console.warn('[SOCKET] Current user was kicked from the room!')
        this.showKickedModal = true
      } else {
        this.processUserLeft(kickedUserId)
      }
    },

    // eslint-disable-next-line no-unused-vars
    handlePublisherKicked(data) {
      // console.log('[SOCKET] Handling publisher_kicked:', data);
    },

    handleIncomingGift(data) {
      // 1. Refresh the gift senders list from API
      this.loadGiftSenders()

      // 2. Trigger animation via ref
      if (data && data.giftRiv) {
        if (this.$refs.giftOverlay) {
          try {
            this.$refs.giftOverlay.play(data.giftRiv)
          } catch (e) {
            console.error('[SOCKET] handleIncomingGift: Error calling play() method:', e)
          }
        } else {
          console.warn('[SOCKET] handleIncomingGift: giftOverlay ref NOT found.')
        }
      } else {
        console.warn('[SOCKET] handleIncomingGift: giftRiv property missing or invalid in event data.', data)
      }

      // 3. Add a notification message to the chat
      if (data.sourceUserName && (data.giftLabel || data.giftProductCode) && data.giftCount) {
        const giftImageSrc = this.resolveGiftImagePathForChat(data.giftImage || data.giftRiv)
        const giftName = data.giftLabel || data.giftProductCode?.split('.').pop() || 'a gift' // Better fallback for name

        const giftMessage = {
          type: 'gift',
          sender: {
            name: data.sourceUserName,
            avatar: data.sourceUserImage || fallbackAvatar,
          },
          gift: {
            name: giftName,
            image: giftImageSrc,
          },
          count: data.giftCount || 1,
        }
        this.messages.push(giftMessage)
      } else {
        console.warn('[SOCKET] gift_sent event missing necessary data for chat message.', data)
      }

      // --- NEW: Dispatch action to update goal progress ---
      this.$store.dispatch('videoRoom/processIncomingGift', data)
      // --- END NEW ---
    },

    // Helper method to resolve gift image path for chat (similar to HostSettingsPanel)
    resolveGiftImagePathForChat(imageIdentifier) {
      if (!imageIdentifier) return ''
      // Example: Assuming image identifier is just the filename like 'heart.svg' or 'heart.riv'
      // and images are in assets/images/gifts/
      const imageName = imageIdentifier.split('.')[0] // Extract name before extension
      try {
        // Prioritize svg or png if needed
        // eslint-disable-next-line global-require,import/no-dynamic-require
        return require(`@/assets/images/gifts/${imageName}.svg`)
      } catch (e) {
        try {
          // eslint-disable-next-line global-require,import/no-dynamic-require
          return require(`@/assets/images/gifts/${imageName}.png`)
        } catch (e2) {
          console.warn(`[Index.vue] Could not resolve gift image for chat: ${imageIdentifier}`)
          // eslint-disable-next-line global-require,import/no-dynamic-require
          return require('@/assets/images/gifts/star.svg') // Example fallback
        }
      }
    },

    handleSendMessage(text) {
      // 1. Get User Info and Socket Connection
      const currentUser = this.$store.state.auth.user
      const userUuid = currentUser?.id
      // eslint-disable-next-line no-unused-vars
      const userName = currentUser?.userName || 'Me'
      // eslint-disable-next-line global-require,no-unused-vars
      const userAvatar = currentUser?.profile?.image || require('@/assets/images/auth/female-avatar.png')

      // 2. Check prerequisites (remains the same)
      if (!this.socketConnection) {
        console.warn('[SOCKET] Cannot send message, connection is not available.')
        return
      }
      if (!userUuid) {
        console.warn('[SOCKET] Cannot send message, user UUID (ID) is missing.')
        return
      }
      if (!text || !text.trim()) {
        console.warn('[SOCKET] Cannot send empty message.')
        return // Don't send empty messages
      }

      // 3. Construct the DTO (remains the same)
      const streamMsgDTO = {
        from: userUuid,
        content: text.trim(),
      }

      // 4. Emit the message via socket (remains the same)
      this.socketConnection.emit('streamMsg', streamMsgDTO)
    },

    openGiftPanel() {
      // Hediye satışı kaldırıldı — hediye paneli açılmıyor
      // this.$refs.giftModal.openChooseGiftModal()
    },

    async handleUserPublished(user, mediaType) {
      try {
        await this.agoraClient.subscribe(user, mediaType)

        if (mediaType === 'audio') {
          if (user.audioTrack) {
            try {
              await user.audioTrack.play()
            } catch (playError) {
              showNotify(this.$toast, this.$t('voiceRoom.audioAutoplayWarning'), 'warning')
            }
          } else {
            console.warn(`[DEBUG_AUDIO] Audio track not found for user ${user.uid} after subscribing.`)
          }
        }
        if (mediaType === 'video') {
          // Assign the track instead of playing it directly
          this.remoteVideoTrack = user.videoTrack
          // Start nudity checks once the remote video track is assigned
          // eslint-disable-next-line no-underscore-dangle
          this._startNudityChecks()
          // if (user.videoTrack && this.$refs.remotePlayerContainer) {
          //   console.log(`[Index.vue] Attempting to play remote video track for user ${user.uid} in ref: remotePlayerContainer`);
          //   user.videoTrack.play(this.$refs.remotePlayerContainer);
          // }
        }
      } catch (subscribeError) {
        console.error(`[DEBUG_AUDIO] Failed to subscribe to user ${user.uid}:`, subscribeError)
      }
    },

    handleUserUnpublished(user, mediaType) {
      // If the user stopped their video and it's the one we are showing, clear it
      // eslint-disable-next-line no-underscore-dangle
      if (mediaType === 'video' && this.remoteVideoTrack && user.uid === this.remoteVideoTrack._originUid) {
        this.remoteVideoTrack = null
      }
    },

    // eslint-disable-next-line no-unused-vars
    handleUserJoined(user) {
      // console.log('User joined:', user.uid)
    },

    async handleUserLeft(user) {
      if (!user) {
        console.warn('[AGORA EVENT] handleUserLeft called with undefined user object. Skipping.')
        return
      }
      const agoraUid = user.uid
      if (!agoraUid) {
        console.warn('[AGORA EVENT] handleUserLeft called, but user.uid is undefined. Skipping.', user)
        return
      }
      this.processUserLeft(agoraUid)

      // Also clear remote video track if the leaving user's video was displayed
      // eslint-disable-next-line no-underscore-dangle
      if (this.remoteVideoTrack && agoraUid === this.remoteVideoTrack._originUid) {
        this.remoteVideoTrack = null
      }
    },

    handleVolumeIndicator(volumes) {
      // Log the raw data from Agora
      // Dispatch an action to process these volumes and update the store
      this.updateSpeakingStatus(volumes)
    },

    async initAgora() {
      if (this.agoraInitialized || !this.roomInfo) {
        console.error(`[DEBUG] initAgora: Skipping initialization. Initialized: ${this.agoraInitialized}, RoomInfo: ${!!this.roomInfo}`)
        return
      }

      try {
        this.agoraClient = initializeAgoraClient(
          this.handleUserPublished,
          this.handleUserUnpublished,
          this.handleUserJoined,
          this.handleUserLeft,
        )

        // Enable Volume Indication AFTER client is created
        AgoraRTC.setParameter('AUDIO_VOLUME_INDICATION_INTERVAL', 200) // Check interval in ms
        this.agoraClient.enableAudioVolumeIndicator()

        // Listen for volume changes
        this.agoraClient.on('volume-indicator', this.handleVolumeIndicator)

        this.agoraInitialized = true

        const userObject = this.$store.state.auth.user
        const userIdentifierForAgora = userObject?.identifier
        if (!userIdentifierForAgora) {
          throw new Error('User identifier for Agora is missing.')
        }

        await joinAgoraChannel(this.agoraClient, this.roomInfo, userIdentifierForAgora)

        if (this.roomInfo.isHost) {
          // eslint-disable-next-line no-underscore-dangle
          await this._initializeAsHost()
        } else {
          // eslint-disable-next-line no-underscore-dangle
          await this._initializeAsAudience()
        }

        // eslint-disable-next-line no-underscore-dangle
        this._startHeartbeat()
        await this.fetchInitialSpeakers()
        await this.loadGiftSenders()

        // Start nudity checks AFTER Agora is initialized and roomInfo is available
        if (this.agoraInitialized && this.roomInfo) {
          // eslint-disable-next-line no-underscore-dangle
          this._startNudityChecks()
        }
      } catch (error) {
        console.error('Agora initialization failed:', error)
        this.agoraInitialized = false
        this.SET_GIFT_SENDERS([])
      }
    },

    // eslint-disable-next-line no-underscore-dangle
    async _initializeAsHost() {
      this.setIsHost(true)
      try {
        await setAgoraClientRole(this.agoraClient, 'host')

        // Publish both audio and video tracks
        // Use Promise.all to run them concurrently for potentially faster setup
        const [audioTrack, videoTrack] = await Promise.all([
          publishLocalAudioTrack(this.agoraClient),
          publishLocalVideoTrack(this.agoraClient),
        ])

        this.localAudioTrack = audioTrack
        this.localVideoTrack = videoTrack

        // Update mute state based on audio track
        if (this.localAudioTrack) {
          this.setIsMuted(!this.localAudioTrack.enabled)
        } else {
          console.warn('Host initialization completed, but localAudioTrack is missing.')
          this.setIsMuted(false)
        }

        // Play video track if available and container exists
        if (this.localVideoTrack && this.$refs.localPlayerContainer) {
          this.localVideoTrack.play(this.$refs.localPlayerContainer)
        } else {
          if (!this.localVideoTrack) console.warn('[Index.vue] Local video track not available after initialization.')
          if (!this.$refs.localPlayerContainer) console.warn('[Index.vue] localPlayerContainer ref not found.')
        }

        // Start nudity checks after host initialization succeeds
        // eslint-disable-next-line no-underscore-dangle
        this._startNudityChecks()
      } catch (error) {
        console.error('Failed to initialize as Host:', error)
        this.setIsHost(false)
        try {
          await setAgoraClientRole(this.agoraClient, 'audience')
        } catch (fallbackError) {
          console.error('Failed to fallback to Audience role:', fallbackError)
        }
      }
    },

    // eslint-disable-next-line no-underscore-dangle
    async _initializeAsAudience() {
      this.setIsHost(false)
      try {
        await setAgoraClientRole(this.agoraClient, 'audience')
        this.setIsMuted(false)
      } catch (error) {
        console.error('Failed to initialize as Audience:', error)
      }
    },

    async handleMicToggle() {
      const desiredEnabledState = this.isMuted // true if currently muted (want to unmute)
      const newMutedState = !desiredEnabledState // The state we are transitioning TO

      if (!this.isHost || !this.localAudioTrack || typeof this.localAudioTrack.setEnabled !== 'function') {
        console.warn('Mic toggle attempted by non-host or track not ready/invalid.')
        return
      }

      try {
        // 1. Toggle local track first
        await this.localAudioTrack.setEnabled(desiredEnabledState)

        // 2. Call the backend service to sync the self-mute status using the remote mute service
        const currentUserIdentifier = this.$store.state.auth.user?.id
        const currentChannelId = this.roomInfo?.channelId

        if (currentUserIdentifier && currentChannelId) {
          try {
            // Pass channel ID, current user ID, and the NEW mute state to the service
            await toggleRemoteMuteService(currentChannelId, currentUserIdentifier)
          } catch (serviceError) {
            console.error('Error calling toggleRemoteMuteService for self:', serviceError)
            // Optional: Notify user about sync failure
            // showNotify(this.$toast, 'Mic status could not be synced with the server.', 'warning');
          }
        } else {
          console.warn('Cannot sync mic status with backend: User identifier or Channel ID not found.')
        }

        // 3. Update local Vuex state AFTER local toggle and backend call attempt
        this.setIsMuted(newMutedState)
      } catch (error) {
        console.error('Error toggling mic locally:', error)
        showNotify(this.$toast, this.$t('voiceRoom.micToggleError'), 'danger', { icon: 'AlertCircleIcon' })
      }
    },

    // eslint-disable-next-line no-underscore-dangle
    _startHeartbeat() {
      if (!this.roomInfo || !this.roomInfo.heartbeatIntervalInSecs) {
        console.error('Heartbeat interval information is missing from roomInfo. Cannot start heartbeat.')
        return
      }

      const intervalSeconds = this.roomInfo.heartbeatIntervalInSecs
      const intervalMilliseconds = intervalSeconds * 1000

      if (this.heartbeatIntervalId) {
        clearInterval(this.heartbeatIntervalId)
      }

      // eslint-disable-next-line no-underscore-dangle
      this._sendHeartbeat()
      // eslint-disable-next-line no-underscore-dangle
      this.heartbeatIntervalId = setInterval(this._sendHeartbeat, intervalMilliseconds)
    },

    // eslint-disable-next-line no-underscore-dangle
    async _sendHeartbeat() {
      if (!this.roomInfo || !this.roomInfo.channelName) {
        console.warn('[Index.vue] Skipping heartbeat: roomInfo or channelName not available.')
        return
      }
      try {
        const heartbeatResponse = await sendHeartbeatRequest(this.roomInfo.channelName)
        // --- Nudity Check from Heartbeat ---
        if (heartbeatResponse && typeof heartbeatResponse.nudity !== 'undefined') {
          this.hasNudity = !!heartbeatResponse.nudity
          if (this.hasNudity) {
            console.warn('[Heartbeat] Nudity flag is true based on heartbeat response.')
            // Optional: Show a persistent warning if needed
          } else {
            // console.log('[Heartbeat] Nudity flag is false.')
          }
        } else {
          // If nudity field is missing in heartbeat, assume false (or keep previous state?)
          // Setting to false for safety, adjust if needed.
          if (this.hasNudity) { // Only log if it was previously true
            // console.log('[Heartbeat] Nudity field missing, resetting hasNudity to false.')
          }
          this.hasNudity = false
        }
        // --- End Nudity Check ---

        // Heartbeat successful, reset the failure counter
        this.heartbeatFailCount = 0

        // console.log('[Index.vue] Heartbeat response received:', heartbeatResponse);
        if (heartbeatResponse && typeof heartbeatResponse.nbOfAudience === 'number') {
          // console.log(`[Index.vue] Updating audience count to: ${heartbeatResponse.nbOfAudience}`);
          this.SET_AUDIENCE_COUNT(heartbeatResponse.nbOfAudience)
        } else {
          // Optional: Log if the audience count is missing or not a number
          // eslint-disable-next-line no-lonely-if
          if (heartbeatResponse) { // Only log if response exists but count is missing/invalid
            console.warn('[Index.vue] Heartbeat response missing or invalid nbOfAudience:', heartbeatResponse)
          }
        }
      } catch (error) {
        // Error is already logged in the service
        console.error('[Index.vue] Error during _sendHeartbeat call:', error)

        // Check if it's a critical error (e.g., 400, 401, 403)
        const status = error.response?.status
        if (status === 400 || status === 401 || status === 403) {
          this.heartbeatFailCount += 1
          console.warn(`[Index.vue] Heartbeat failed with status ${status}. Count: ${this.heartbeatFailCount}`)
          if (this.heartbeatFailCount >= 4) {
            console.error('[Index.vue] Heartbeat failed 4 consecutive times. Ending stream...')
            this.endStream() // Call endStream to close the room
          }
        } else {
          // If it's another type of error (e.g., network error), reset the counter
          this.heartbeatFailCount = 0
        }
        // Consider stopping heartbeat interval if it fails consistently, even non-400 errors?
        // clearInterval(this.heartbeatIntervalId);
        // this.heartbeatIntervalId = null;
      }
    },

    async endStream() {
      if (this.isEndingStream) {
        return
      }
      this.isEndingStream = true

      // Clear heartbeat immediately
      if (this.heartbeatIntervalId) {
        clearInterval(this.heartbeatIntervalId)
        this.heartbeatIntervalId = null
      }

      let leaveSuccess = false
      try {
        if (this.isHost) {
          // Host ends the stream for everyone
          leaveSuccess = await endStreamService(this.agoraClient, this.localAudioTrack, this.localVideoTrack)
          if (!leaveSuccess) {
            console.error('endStreamService (Host) reported failure.')
          }
        } else {
          // Audience just leaves the Agora channel
          leaveSuccess = await leaveRoomAsAudienceService(this.agoraClient)
          if (!leaveSuccess) {
            console.error(' leaveRoomAsAudienceService reported failure.')
          }
        }

        // Common cleanup regardless of role, only if leaving Agora succeeded
        if (leaveSuccess) {
          this.$store.dispatch('videoRoom/setRoomInfo', null)
          // No need to set isHost/isMuted here, setRoomInfo action handles it
          if (this.isHost) {
            this.$router.push({ name: 'video-room-setup' })
          } else {
            this.$router.push({ name: 'popular-users' }) // Redirect audience to popular users
          }
        } else {
          // Maybe re-enable heartbeat if leaving failed?
        }
      } catch (error) {
        // Catch errors from the service calls themselves (e.g., network issues)
        console.error('Unexpected error during endStream/leaveRoom:', error)
        leaveSuccess = false // Ensure cleanup doesn't run on unexpected error
      } finally {
        this.isEndingStream = false
      }
    },

    async approveRequest(userId) {
      if (!userId) {
        console.error('approveRequest called with invalid userId.')
        return
      }
      const request = this.pendingSpeakerRequests.find(r => r.userId === userId)
      if (!request || !request.userUuid) {
        console.error(`Cannot find request or userUuid for user ID: ${userId}`)
        if (this.$refs.speakerRequestModal) this.$refs.speakerRequestModal.hideModal()
        return
      }

      try {
        const success = await approveSpeakerRequestService(request.userUuid)
        if (success) {
          const speakerData = {
            id: request.userId,
            name: request.userName,
            avatar: request.userAvatar || fallbackAvatar,
            uuid: request.userUuid,
            isActive: true,
            isMuted: false,
            agoraUid: null, // Agora UID might come later via socket event
          }
          this.handleSpeakerRequestApproved({ userId: request.userId, speakerData })
          // showNotify(this.$toast, `İstek onaylandı (ID: ${userId})`, 'success');
        } else {
          console.warn(`Approve request service call failed for user: ${userId}`)
          // Still remove the request from the list on server failure?
          this.handleSpeakerRequestRejected(userId) // Remove locally if server fails?
        }
      } catch (error) {
        console.error(`Error approving request for user ${userId}:`, error)
        this.handleSpeakerRequestRejected(userId)
      } finally {
        // --- Close Modal ---
        if (this.$refs.speakerRequestModal) {
          this.$refs.speakerRequestModal.hideModal()
        }
        // --- End Close Modal ---
      }
    },

    async rejectRequest(userId) {
      if (!userId) {
        console.error('rejectRequest called with invalid userId.')
        return
      }
      const request = this.pendingSpeakerRequests.find(r => r.userId === userId)
      if (!request || !request.userUuid) {
        console.error(`Cannot find request or userUuid for user ID: ${userId}`)
        if (this.$refs.speakerRequestModal) this.$refs.speakerRequestModal.hideModal()
        return
      }

      try {
        const success = await rejectSpeakerRequestService(request.userUuid)
        if (success) {
          this.handleSpeakerRequestRejected(userId)
        } else {
          console.warn(`Reject request service call failed for user: ${userId}`)
          this.handleSpeakerRequestRejected(userId) // Remove locally if server fails?
        }
      } catch (error) {
        console.error(`Error rejecting request for user ${userId}:`, error)
        this.handleSpeakerRequestRejected(userId)
      } finally {
        // --- Close Modal ---
        if (this.$refs.speakerRequestModal) {
          this.$refs.speakerRequestModal.hideModal()
        }
        // --- End Close Modal ---
      }
    },

    async becomePublisher() {
      if (this.isHost || !this.agoraClient) {
        console.warn('Already host or Agora client not ready. Aborting role change.')
        return
      }
      try {
        await setAgoraClientRole(this.agoraClient, 'host')
        // eslint-disable-next-line no-undef
        const tracks = await publishLocalTracks(this.agoraClient)
        this.localAudioTrack = tracks.audioTrack
        this.localVideoTrack = tracks.videoTrack

        this.setIsHost(true)
        if (this.localAudioTrack) {
          this.setIsMuted(!this.localAudioTrack.enabled)
        } else {
          console.warn('Became publisher via service, but new track is missing.')
          this.setIsMuted(false)
        }
        if (this.localVideoTrack && this.$refs.localPlayerContainer) {
          this.localVideoTrack.play(this.$refs.localPlayerContainer)
        }
        showNotify(this.$toast, this.$t('Promoted to speaker'), 'success')
      } catch (error) {
        console.error('Error becoming publisher via service:', error)
        this.setIsHost(false)
      }
    },

    async fetchInitialSpeakers() {
      if (!this.roomInfo || !this.roomInfo.channelId || !this.roomInfo.mainPublisherUuid) {
        console.error('Cannot fetch speakers: Required roomInfo fields missing.')
        return
      }

      const { channelId, mainPublisherUuid } = this.roomInfo

      try {
        const speakersData = await getInitialSpeakersData(channelId, mainPublisherUuid)

        this.SET_MAIN_HOST(speakersData.mainHost)
        this.setSpeakers(speakersData.speakers)
      } catch (error) {
        console.error('Error calling getInitialSpeakersData service:', error)
        this.SET_MAIN_HOST(null)
        this.setSpeakers([])
      }
    },

    // eslint-disable-next-line no-unused-vars
    handleMuteParticipant(participantId) {
      // console.log(`Request to MUTE participant ID: ${participantId}`);
    },

    // eslint-disable-next-line no-unused-vars
    handleUnmuteParticipant(participantId) {
      // console.log(`Request to UNMUTE participant ID: ${participantId}`);
    },

    // eslint-disable-next-line no-unused-vars
    handleKickParticipant(participantId) {
      // console.log(`Received kick request for participant ID: ${participantId}. This handler might be deprecated.`);
    },

    async fetchAndSetRoomDetails() {
      if (!this.roomInfo || !this.roomInfo.channelId) {
        console.error('Cannot fetch room details: roomInfo or channelName missing.')
        this.setRoomDetails({ streamTitle: this.$t('Error'), goals: [] })
        return
      }
      try {
        const response = await getVideoRoomGoalsService(this.roomInfo.channelId)
        if (response && response.streamTitle && response.goals) {
          this.setRoomDetails({
            streamTitle: response.streamTitle,
            goals: response.goals,
          })
        } else {
          console.warn('Invalid room details response received:', response)
          this.setRoomDetails({ streamTitle: this.$t('No Title'), goals: [] })
        }
      } catch (error) {
        console.error('Error fetching room details:', error)
        this.setRoomDetails({ streamTitle: this.$t('No Title'), goals: [] })
      }
    },

    async handleVoiceStateToggled(data) {
      const { userId } = data
      const newMuteState = data.voiceMuted

      if (userId === undefined || newMuteState === undefined) {
        // console.warn('[SOCKET] voice_state_toggled event missing userId or voiceMuted state.', data)
        return
      }

      // --- Apply local mute/unmute via Agora SDK if this client is the target ---
      const currentUserId = this.$store.state.auth.user?.identifier
      if (userId === currentUserId) {
        if (this.localAudioTrack && typeof this.localAudioTrack.setEnabled === 'function') {
          try {
            const desiredEnabledState = !newMuteState
            await this.localAudioTrack.setEnabled(desiredEnabledState)

            this.setIsMuted(newMuteState)
          } catch (error) {
            console.error(`Error calling localAudioTrack.setEnabled(${!newMuteState}):`, error)
          }
        } else {
          console.warn('Cannot apply local mute/unmute: localAudioTrack is not available or invalid.')
        }
      }
      // --- End of local Agora action ---

      // Dispatch action to update the store for UI consistency for everyone
      this.updateSpeaker({ id: userId, isMuted: newMuteState })
    },

    async loadGiftSenders() {
      if (!this.roomInfo || !this.roomInfo.channelId || !this.hostAgoraUid) {
        console.error('Cannot load gift senders: roomInfo, channelId or hostAgoraUid missing.')
        this.SET_GIFT_SENDERS([])
        return
      }
      try {
        const senders = await getGiftSendersListService(this.roomInfo.channelId, this.hostAgoraUid)
        this.SET_GIFT_SENDERS(senders)
      } catch (error) {
        console.error('Error loading gift senders:', error)
        this.SET_GIFT_SENDERS([])
      }
    },

    // New method to handle quick gift sending
    async handleSendQuickGift(gift) {
      if (!gift || !gift.productCode) {
        console.error('Invalid gift data received for quick send.')
        return
      }

      const giftData = { productCode: gift.productCode }

      try {
        // Call the service function and get the response
        const response = await sendVideoRoomGift(giftData)

        // Check the success flag in the response body
        if (response && response.success === true) {
          // Gift sent successfully - trigger animation
          if (gift.riv && this.$refs.giftOverlay) {
            this.$refs.giftOverlay.play(gift.riv)
          }
        } else if (response && response.success === false) {
          // Gift sending failed (likely insufficient funds)
          console.warn('Gift sending failed (success: false). Response:', response)
          if (response.marketProductCode && this.$refs.purchaseCreditsModal) {
            const purchaseUrl = `/payment/products/${response.marketProductCode}`
            // Commented out modal display
            // this.$refs.purchaseCreditsModal.show(purchaseUrl, this.$t('videoRoom.insufficientBalanceTitle') || 'Yetersiz Bakiye');

            // Open market URL in a new tab instead
            window.open(purchaseUrl, '_blank')
          } else {
            console.error('Cannot open purchase modal: marketProductCode or modal ref missing.')
            // Show a generic error if modal can't be opened
            throwDefaultError(this.$toast, new Error(this.$t('videoRoom.insufficientBalanceError') || 'Hediye gönderilemedi. Lütfen bakiyenizi kontrol edin.'))
          }
        } else {
          // Handle unexpected response structure
          console.error('Unexpected response structure from sendVideoRoomGift:', response)
          throwDefaultError(this.$toast, new Error(this.$t('There was an unexpected error')))
        }
      } catch (error) {
        console.error('Error sending quick gift:', error)
        throwDefaultError(this.$toast, error) // Use the helper for consistent error display
      }
    },

    // New method to handle modal close and redirect
    handleKickedModalClose() {
      this.showKickedModal = false
      this.$router.push({ name: 'popular-users' })
    },

    // eslint-disable-next-line no-underscore-dangle
    _startNudityChecks() {
      if (!this.isHost) {
        return // Do not start checks if not the host
      }

      if (this.nudityCheckStarted) return // Only start once

      if (this.nudityCheckIntervalId) {
        clearInterval(this.nudityCheckIntervalId)
        this.nudityCheckIntervalId = null
      }

      // Use interval from roomInfo, fallback to 5 seconds if not available
      const intervalSeconds = this.roomInfo?.nudityCheckIntervalInSecs || 5
      this.nudityCheckIntervalMs = intervalSeconds * 1000

      if (this.nudityCheckIntervalMs > 0) {
        // Small delay to ensure VideoPlayer ref is definitely ready
        setTimeout(() => {
          if (!this.$refs.backgroundVideoPlayer) {
            console.error('Cannot start nudity checks: backgroundVideoPlayer ref still not available after delay.')
            return
          }
          this.nudityCheckIntervalId = setInterval(this.performNudityCheck, this.nudityCheckIntervalMs)
          this.nudityCheckStarted = true // Set flag after starting
        }, 500) // Add a small delay (e.g., 500ms)
      } else {
        console.warn('Nudity check interval is zero or invalid. Checks disabled.')
      }
    },

    async performNudityCheck() {
      if (!this.$refs.backgroundVideoPlayer || !this.roomInfo?.channelId) {
        console.warn('Skipping nudity check: Video player ref or channelId not available.')
        return
      }

      const base64Data = await this.$refs.backgroundVideoPlayer.captureFrame()
      if (!base64Data) {
        // console.warn('Skipping nudity check: Failed to capture frame.')
        return
      }

      let userIdToCheck = null
      if (this.isHost) {
        userIdToCheck = this.$store.state.auth.user?.identifier
      } else if (this.remoteVideoTrack) {
        // Use Agora UID from the remote track being displayed
        // eslint-disable-next-line no-underscore-dangle
        userIdToCheck = this.remoteVideoTrack._originUid
      }

      if (!userIdToCheck) {
        console.warn('Skipping nudity check: Could not determine user ID to check.')
        return
      }
      const { channelId } = this.roomInfo

      try {
        // --- IMPORTANT: Adjust service call based on actual backend endpoint ---
        // Assuming checkNudity takes channelId (as callId), userId, image, mime_type
        const body = await sendCheckNudity(
          channelId,
          userIdToCheck,
          base64Data,
          'image/png', // Explicitly pass mime type
        )
        // --- End Service Call Assumption ---

        if (body) {
          this.hasNudity = !!body.is_nudity_exists // Update based on response

          if (body.is_child_exists) {
            this.hasNudity = true // Ensure blur/other effects apply
            console.error('Child detected! Ending stream.')
            if (this.nudityCheckIntervalId) clearInterval(this.nudityCheckIntervalId) // Stop further checks

            // Define the action to take after the modal is closed
            const afterModalClose = async () => {
              if (this.isEndingStream) return // Prevent double execution
              this.isEndingStream = true
              try {
                // Call the appropriate service based on user role (like in endStream)
                let leaveSuccess = false
                if (this.isHost) {
                  leaveSuccess = await endStreamService(this.agoraClient, this.localAudioTrack, this.localVideoTrack)
                } else {
                  leaveSuccess = await leaveRoomAsAudienceService(this.agoraClient)
                }

                if (!leaveSuccess) {
                  console.error('[afterModalClose] Agora leave/end service reported failure.')
                  // Optionally handle the failure case, though we still proceed to cleanup/redirect
                }
              } catch (leaveError) {
                console.error('Error leaving Agora channel after child detection:', leaveError)
              } finally {
                // Always clean up store and redirect
                this.$store.dispatch('videoRoom/setRoomInfo', null) // Clean Vuex state
                this.$router.push({ name: 'popular-users' })
                this.isEndingStream = false // Reset flag
              }
            }

            // Show the generic error modal
            if (this.$refs.errorModal) {
              this.$refs.errorModal.openModal(
                this.$t('videoRoom.streamTerminatedTitle'),
                this.$t('videoRoom.childDetectedMessage'),
                afterModalClose, // Pass the callback function
              )
            } else {
              console.error('GenericErrorModal ref not found! Cannot display termination message.')
              // Fallback: directly call the cleanup function if modal fails
              afterModalClose()
            }
          }
        }
      } catch (error) {
        console.error('Error during nudity check API call:', error)
        // Decide if UI should be notified about check failure
        // showNotify(this.$toast, 'Nudity check failed.', 'warning');
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.video-room {
  position: relative; // Needed for absolute positioning of the background
  height: 90dvh; // Use dynamic viewport height for better mobile handling
  background: #13071C; // Fallback background
  color: white;
  padding: 0; // Remove padding if any
  display: flex;
  flex-direction: column;
  overflow: hidden; // Prevent main scrollbars
}

.video-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0; // Behind foreground content
}

.foreground-content {
  position: relative; // To stack above the background
  z-index: 1;
  flex: 1; // Allow this content area to grow/shrink if .video-room is flex
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// Adjust previous styles if they conflict

// Ensure header/speakers/chat are inside foreground-content for padding
.video-room-header,
.video-room-speakers {
  flex-shrink: 0;
}

.video-room-chat {
  margin-top: auto; // Pushes chat to bottom within foreground-content
  flex-shrink: 0;
}

.speaker-requests-temp {
  background-color: rgba(0, 0, 0, 0.3);
  padding: 10px;
  margin: 10px 20px;
  border-radius: 5px;
  color: white;

  h5 {
    margin-bottom: 5px;
    font-size: 1rem;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  li {
    padding: 5px 0;
    display: flex;
    align-items: center;
  }
}
</style>
